import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DeleteProjectVersionByIdParams,
  DeleteProjectVersionByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface DeleteProjectVersionByIdState
  extends MergeRequestState<DeleteProjectVersionByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.deleteProjectVersionById,
  isFetching: (state: ReduxState) =>
    state.server.deleteProjectVersionById.isFetching,
  data: (state: ReduxState) => state.server.deleteProjectVersionById.data,
  error: (state: ReduxState) => state.server.deleteProjectVersionById.error,
};

const initialState: DeleteProjectVersionByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/deleteProjectVersionById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: DeleteProjectVersionByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) =>
    serverApi.deleteProjectVersionById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerDeleteProjectVersionById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const deleteProjectVersionById: ServerDeleteProjectVersionById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
